// Pair hero card (W36.0, spec §3.1) — dual avatars, days together, today's
// message, review entry.
//
// Layout follows spec §3.1: the left column is the big number, the right
// column is the message block. The two are separated by a hairline rather than
// a gap so they read as one card with two halves (the mockup's `.hsplit` uses
// `border-left`; this keeps that structure with token values).
//
// The message block is the whole hit target — spec §3.2 requires the entire
// block to open the editor with a ≥44px target, so the `<button>` wraps the
// label, the attribution chip and the body text rather than only the pencil
// icon. A pencil affordance was the mockup's version and it is the classic
// dead-tap shape: a 28px target that reads as the action.

import { Link } from "react-router-dom";
import { ChevronRight, MessageSquare, Pencil } from "lucide-react";

import { AuthorAvatar } from "@/components/primitives/AuthorAvatar";
import { HeroCard } from "@/components/primitives/HeroCard";
import { HERO_DASHBOARD } from "@/lib/screenType";
import type { DashboardMessage, DashboardPairMember } from "@/lib/api/dashboard";
// Profile spec §8 B3/B2: one resolver for avatar URLs, and it tolerates the
// null that means "no avatar" without the card having to know that.
import { avatarUrl } from "@/lib/profile/api";
import { DASHBOARD_I18N } from "@/lib/i18n/dashboard";
import { cn } from "@/lib/utils/cn";
import { resolvePairSides } from "./dashboardHelpers";

export interface PairHeroCardProps {
  pairName: string;
  relationshipStart: string;
  daysTogether: number;
  message: DashboardMessage;
  onEditMessage: () => void;
  /** Total archived messages — 「共 N 則」. */
  messageTotal: number;
  /** True when the partner overwrote it and we have not re-opened (§3.3). */
  showPartnerUpdated: boolean;
  /**
   * The pair's two members, ordered `(owner, partner)` (W36.1).
   *
   * Optional so a caller without them still renders — the card falls back to
   * the tone-coloured initial it always showed. But **when present they are the
   * real names and the real avatars**: until W36.1 the two `AuthorAvatar`s here
   * were hardcoded to `label="我"` / `label="對方"`, so a couple who both set a
   * photograph in the profile module still saw the placeholders on the first
   * screen they open.
   *
   * W57.0: the **order is not the viewer's**. The payload sorts by role
   * (`owner` first), and the owner is a property of the pair, not of the
   * session — so `members[0]` is the wrong person for a partner who logged
   * in. The card therefore takes `currentUserId` and resolves the two sides
   * through `resolvePairSides`; see that function for the whole story.
   */
  members?: DashboardPairMember[];
  /** The signed-in member's id — decides which member is 「我」. */
  currentUserId?: string | null;
}

export function PairHeroCard({
  pairName,
  relationshipStart,
  daysTogether,
  message,
  onEditMessage,
  messageTotal,
  showPartnerUpdated,
  members = [],
  currentUserId = null,
}: PairHeroCardProps) {
  const hasMessage = Boolean(message.content);
  const { me, partner } = resolvePairSides(members, currentUserId);

  return (
    <HeroCard slot={HERO_DASHBOARD} testId="pair-hero-card">
      <div className="flex items-center gap-s-3">
        <div className="flex shrink-0 -space-x-2" data-testid="hero-avatars">
          {" "}
          <AuthorAvatar
            tone="you"
            size="md"
            label={me?.display_name ?? "我"}
            src={avatarUrl(me?.avatar_url)}
          />
          <AuthorAvatar
            tone="partner"
            size="md"
            label={partner?.display_name ?? "對方"}
            src={avatarUrl(partner?.avatar_url)}
          />
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-h3 font-semibold text-ink-1">{pairName}</p>
          <p className="truncate text-meta text-ink-1/70">
            {DASHBOARD_I18N.hero.sinceFrom.replace("{date}", relationshipStart)}
          </p>
        </div>
      </div>

      {/* W57.0 — below 360px the two halves **stack**.
          At 320px the right column was ~17px of text width: 「回顧我們的每一天」
          broke into four lines and 「還沒有記錄」 sat in the middle of them.
          That is the app's own narrow sweep (`mockup/w47.9-narrow-dashboard.png`),
          not a mockup artefact — the `shrink-0` on the count kept that one on
          a single line and squeezed the *label* instead.
          At 390px the same row breaks into two lines, which is fine, so the
          split stays side by side from `xs` (360px) up. */}
      <div
        data-testid="hero-split"
        className="mt-s-5 flex flex-col items-stretch gap-s-4 xs:flex-row xs:items-start"
      >
        <div className="shrink-0">
          <p className="text-meta text-ink-1/70">{DASHBOARD_I18N.hero.daysTogether}</p>
          {/* DESIGN §3.3 — pure numeric scenario. */}
          {/* W57.0 — `data-testid` so a test can assert the number appears
              **once** on the screen. It used to appear twice (PageHero's
              subtitle and here) and nothing said so. */}
          <p
            data-testid="days-together"
            className="text-display-2 font-semibold tabular-nums text-you-strong"
          >
            {daysTogether}
            <span className="ml-s-1 text-body-sm font-semibold text-ink-1/80">
              {DASHBOARD_I18N.hero.daysUnit}
            </span>
          </p>
        </div>

        <div className="min-w-0 flex-1 border-t border-hairline pt-s-4 xs:border-l xs:border-t-0 xs:pl-s-4 xs:pt-0">
          <button
            type="button"
            onClick={onEditMessage}
            aria-label={DASHBOARD_I18N.message.edit}
            data-testid="hero-message-block"
            // h-11 = 44px min target per DESIGN §15 (asserted on the class:
            // jsdom has no layout engine, so rects are always zero).
            className={cn(
              "press flex min-h-11 w-full flex-col items-start gap-s-1 rounded-md",
              "text-left hover:bg-black/5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-you-strong focus-visible:outline-offset-2",
            )}
          >
            <span className="flex w-full items-center gap-s-2">
              <MessageSquare className="h-3.5 w-3.5 shrink-0 text-ink-1/70" aria-hidden />
              <span className="text-meta text-ink-1/70">{DASHBOARD_I18N.message.label}</span>
              {showPartnerUpdated ? (
                <span
                  data-testid="hero-message-updated-chip"
                  className="rounded-pill bg-gold-tint px-2 py-0.5 text-meta font-semibold text-gold-ink"
                >
                  {DASHBOARD_I18N.message.authorPartner}更新過
                </span>
              ) : null}
              <Pencil className="ml-auto h-3.5 w-3.5 shrink-0 text-ink-1/70" aria-hidden />
            </span>
            <span
              data-testid="hero-message-text"
              className={cn(
                "line-clamp-3 text-body-sm leading-relaxed",
                hasMessage ? "text-ink-1" : "text-ink-1/70",
              )}
            >
              {hasMessage ? message.content : DASHBOARD_I18N.message.writeFirst}
            </span>
          </button>

          {/* Review entry — lives inside the hero per spec §2.6 R3.3, not as a
              separate card. */}
          <Link
            to="/dashboard/messages"
            data-testid="hero-review-link"
            className={cn(
              // `w-full` is explicit rather than inherited from flex-stretch:
              // the tap target's width is a DESIGN §15 requirement, so it should
              // not depend on the parent's `align-items` staying at its default.
              "press mt-s-3 flex min-h-11 w-full items-center gap-s-2 border-t border-hairline pt-s-3",
              "text-ink-1/80 focus-visible:outline focus-visible:outline-2 focus-visible:outline-you-strong focus-visible:outline-offset-2",
            )}
          >
            {/*
              No `truncate` here. At 320px this row carries an icon, an
              8-character label, the count and a chevron, and the label — the
              only part that says what tapping leads to — was down to 78px of
              the 112px it needs, losing 30%. Hiding 30% of a label is past
              the point where an ellipsis abbreviates, and the label is the
              one item in the row that cannot be dropped: the count beside it
              is already `shrink-0`. Letting it wrap costs one line on the
              narrowest phones and loses nothing.
            */}
            <span className="min-w-0 flex-1 text-body-sm font-semibold">
              {DASHBOARD_I18N.message.review}
            </span>
            <span className="shrink-0 text-meta text-ink-1/70 tabular-nums">
              {messageTotal > 0
                ? DASHBOARD_I18N.message.reviewCount.replace("{count}", String(messageTotal))
                : DASHBOARD_I18N.message.reviewEmpty}
            </span>
            <ChevronRight className="h-4 w-4 shrink-0 text-ink-1/70" aria-hidden />
          </Link>
        </div>
      </div>
    </HeroCard>
  );
}
